import type { SortDescriptor } from "react-stately";

import { getRouteApi } from "@tanstack/react-router";

import { FeaturePageLayout } from "@/features/shared/layout";
import { usePageTitle } from "@/hooks/usePageTitle";

import { AiCostsPeriodFilter } from "./AiCostsPeriodFilter";
import {
  AI_COSTS_DEFAULT_SORT,
  AI_COSTS_TABS,
  type AiCostsBreakdownTab,
  type AiCostsSearch,
  type AiCostsTab,
  resolveAiCostsRange,
  toCustomDates,
} from "./aiCostsSearch";
import { AiCostsTable } from "./AiCostsTable";
import { AiCostsTotals } from "./AiCostsTotals";
import { useAiCosts } from "./hooks/useAiCosts";
import { ImageModelsTab } from "./image-models/ImageModelsTab";

const route = getRouteApi("/_auth/_app-shell/admin/ai-costs");

const NAME_HEADERS: Record<AiCostsBreakdownTab, string> = {
  accounts: "Account",
  features: "Feature",
  models: "Model",
  "usage-type": "Usage type",
};

export function AiCostsPage() {
  usePageTitle("AI costs");

  const search = route.useSearch();
  const navigate = route.useNavigate();

  return (
    <div className="p-xl">
      <FeaturePageLayout
        tabs={{
          className: "bg-bg-primary",
          onSelectionChange: (key) =>
            navigate({
              search: {
                ...search,
                dir: "desc",
                page: 1,
                sort: AI_COSTS_DEFAULT_SORT,
                tab: key as AiCostsTab,
              },
            }),
          selectedKey: search.tab,
          tabs: [...AI_COSTS_TABS],
        }}
        title="AI costs"
      >
        {search.tab === "image-models" ? (
          <ImageModelsTab />
        ) : (
          <AiCostsBreakdown tab={search.tab} />
        )}
      </FeaturePageLayout>
    </div>
  );
}

function AiCostsBreakdown({ tab }: { tab: AiCostsBreakdownTab }) {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const range = resolveAiCostsRange(search);
  const costs = useAiCosts(tab, range);

  const sortDescriptor: SortDescriptor = {
    column: search.sort,
    direction: search.dir === "asc" ? "ascending" : "descending",
  };

  const updateSearch = (changes: Partial<AiCostsSearch>) =>
    navigate({ search: { ...search, page: 1, ...changes } });

  return (
    <>
      <div className="flex flex-col gap-6 px-xl py-6">
        <AiCostsPeriodFilter
          onChange={(period) =>
            updateSearch(
              period.period === "custom" && !period.from && range
                ? { ...period, ...toCustomDates(range) }
                : period,
            )
          }
          value={search}
        />

        {costs.error && (
          <p className="text-sm text-text-error" role="alert">
            Could not load AI costs: {costs.error.message}
          </p>
        )}

        {costs.data && <AiCostsTotals totals={costs.data.totals} />}
      </div>

      <AiCostsTable
        isLoading={costs.isLoading}
        nameHeader={NAME_HEADERS[tab]}
        onPageChange={(page) => updateSearch({ page })}
        onSortChange={({ column, direction }) =>
          updateSearch({
            dir: direction === "ascending" ? "asc" : "desc",
            sort: column as AiCostsSearch["sort"],
          })
        }
        page={search.page}
        rows={costs.data?.rows ?? []}
        sortDescriptor={sortDescriptor}
      />
    </>
  );
}
